import { AxiosResponse } from 'axios';
import { addDays, format } from 'date-fns';
import qs from 'qs';
import { trackPromise } from 'react-promise-tracker';
import { CompetitorPriceDTO, CompetitorsInfo, ExpandedRowPrice, IGerenciadorPrecosDatapoint as RowData, UnknownObject } from '../../../@types';
import { IPAMaths, api } from '../../../lib';
import { withPendingEditedPrice } from '../../../store/priceManagerStore';
import { RuleModelReturn } from '../Estrategia/pages/RegrasDePreco/lib';
import { PriceManagerDatapointsModel } from './hooks/usePriceManagerDatapointsModel';
import { TotalPriceDataType, VariationPriceDataType } from './Components/BigNumbersArea/BigNumbersArea.types';
import { GET_ALL_PRODUCTS, GET_COMPETITORS_INFO, GET_COMPETITORS_INFO_OFFER_PRICE } from './utils';
import { getValidData } from './utils/getValidData';

export type LoadFilterDataOption = {
  isModal?: boolean;
  size?: number;
  productId?: string[];
  productFamilyId?: string[];
  gtin?: string[];
  description?: string[];
  abcClass?: string[];
  categoryLevel1?: string[];
  categoryLevel2?: string[];
  categoryLevel3?: string[];
  categoryLevel4?: string[];
  categoryLevel5?: string[];
  categoryLevel6?: string[];
  categoryLevel7?: string[];
};

export const loadFilterDataOptionDefault: LoadFilterDataOption = {
  isModal: false,
  size: 20,
};

export const salvarSimulacao = (data: any) =>
  trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/simulation/forecast/save`,
      method: 'POST',
      expectToken: true,
      data: JSON.stringify(data),
    }).then(resp => resp),
  );

export const novoCalculoPrevisao = (currentPrice: any, newPrice: any, storeId: any, productId: any, pmz: any) =>
  trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/simulation/forecast`,
      method: 'GET',
      expectToken: true,
      params: {
        currentPrice,
        newPrice,
        storeId,
        productId,
        pmz,
      },
    }).then(resp => resp),
  );

export type ApplyPricesParams = {
  selectedAllProducts: boolean;
  applyToFamily: boolean;
  productsSelect: {
    productId: RowData['productId'];
    storeId: RowData['storeId'];
    price: RowData['price'];
    productsToBePricedId: RowData['productsToBePricedId'];
  }[];
  productsUnSelect: {
    productId: RowData['productId'];
    storeId: RowData['storeId'];
    productsToBePricedId: RowData['productsToBePricedId'];
  }[];
  filters?: UnknownObject;
  datapointExhibitionType: 'PRODUCT' | 'FAMILY' | 'PRODUCT_CLUSTER' | 'PRODUCT_CLUSTER_FAMILY';
  /** yyyy-MM-dd. Só enviado com a flag de data de vigência ligada. */
  effectiveStartDate?: string;
};

export const applyPrices = async (params: ApplyPricesParams) => {
  const res = await withPendingEditedPrice(() =>
    trackPromise(
      api.patch('/all-products/apply', params, {
        expectToken: true,
      }),
      'get-datapoints-revisao',
    ),
  );

  return res;
};

export type PriceApplicationPermissionResponse = {
  allowed: boolean;
};

export const getPriceApplicationPermission = async (): Promise<PriceApplicationPermissionResponse> => {
  const response = await api.get<PriceApplicationPermissionResponse>('/setup/pricing-permission/price-application/me', {
    expectToken: true,
  });

  return response.data;
};

export type GetPriceCompositionParams = {
  productId: RowData['productId'];
  storeId: RowData['storeId'];
  clusterId?: RowData['clusterId'];
  datapointExhibitionType?: 'PRODUCT' | 'FAMILY' | 'PRODUCT_CLUSTER' | 'PRODUCT_CLUSTER_FAMILY';
};

export const getPriceComposition = ({ productId, storeId, clusterId, datapointExhibitionType }: GetPriceCompositionParams) =>
  trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/all-products/price/composition`,
      method: 'GET',
      expectToken: true,
      params: getValidData({
        productId,
        storeId,
        ...(clusterId && { clusterId }),
        ...(datapointExhibitionType && { datapointExhibitionType }),
      }),
      paramsSerializer(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    }).then(response => response.data),
    'price-composition',
  );

export type IGetCompetitorsInfo = {
  productId: RowData['productId'];
  storeId: RowData['storeId'];
  productsToBePricedId: RowData['productsToBePricedId'];
  competitorDays: RowData['competitorDays'];
  productFamilyId?: RowData['productFamilyId'];
  competitorStoreId?: CompetitorPriceDTO['competitorStoreId'][];
  unselectedCompetitors: RuleModelReturn['competitors'];
  clusterId: string | null;
  clusterRule?: boolean;
  factorProductMaster?: RowData['factorProductMaster'];
};

export namespace GetCompetitorsInfo {
  export type Params = IGetCompetitorsInfo;
  export type Result = CompetitorsInfo;
}

export const getCompetitorsInfo = async (params: GetCompetitorsInfo.Params): Promise<GetCompetitorsInfo.Result> => {
  const { url, config } = GET_COMPETITORS_INFO(params);

  const { data } = await trackPromise(api.get<CompetitorsInfo>(url, config), `competitor-row-${params.storeId}-${params.productId}`);

  return data;
};

export const getCompetitorsInfoOfferPrice = async (params: GetCompetitorsInfo.Params): Promise<GetCompetitorsInfo.Result> => {
  const { url, config } = GET_COMPETITORS_INFO_OFFER_PRICE(params);

  const { data } = await trackPromise(api.get<CompetitorsInfo>(url, config), `competitor-row-${params.storeId}-${params.productId}`);

  return data;
};

const removeEmptyFilterValues = (data: Record<string, unknown>) =>
  Object.entries(data).reduce<Record<string, unknown>>((params, [key, value]) => {
    if (Array.isArray(value) && !value.length) return params;
    if (value === undefined || value === null || value === '') return params;
    return {
      ...params,
      [key]: value,
    };
  }, {});

export const getFamiliaProdutos = (productFamilyId = '', { size = 20, isModal = false, ...filters }: LoadFilterDataOption = loadFilterDataOptionDefault) =>
  trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/products/families/id`,
      method: 'POST',
      expectToken: true,
      params: {
        size: isModal ? 1000 : size,
      },
      data: removeEmptyFilterValues({
        ...filters,
        ...(productFamilyId && {
          query: productFamilyId,
        }),
      }),
    }).then(resp => resp?.data),
  );

export const getLojasRevisao = (search: any) =>
  trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/products/store`,
      method: 'GET',
      expectToken: true,
      params: {
        size: 20,
        ...(search && {
          search,
        }),
      },
    }).then(resp => resp?.data),
  );

export namespace GetDatapointsRevisao {
  export type Params = UnknownObject;
  export type Response = {
    content: RowData[];
    number: number;
    size: number;
    totalElements: number;
    hasContent: boolean;
    first: boolean;
    last: boolean;
    numberOfElements: number;
    totalPages: number;
    pageable: {
      sort: any[];
      offset: number;
      pageSize: number;
      pageNumber: number;
      paged: boolean;
      unpaged: boolean;
    };
    empty: boolean;
  };
}

export const onSuccessGetDatapointsRevisaoItem = (rowData: RowData): RowData => {
  const offerDetails = rowData.offerDetails
    ? {
        ...rowData.offerDetails,
        newCompetitivenessPrice: IPAMaths.novaCompetitividade(rowData.offerDetails.price ?? null, rowData.offerDetails.competitorsPrice ?? null),
      }
    : rowData.offerDetails;

  const { price, competitorsPrice, retailPrice, salesTaxPercentage, expensesPercent, cost, wholesale } = rowData;

  const novaCompetitividade = IPAMaths.novaCompetitividade(price, competitorsPrice);

  const retailCpi = IPAMaths.novaCompetitividade(retailPrice, competitorsPrice);

  const novaMargem = IPAMaths.novaMargem(price, salesTaxPercentage, expensesPercent, cost);

  const wholesaleCpi = IPAMaths.precoWithCpi(wholesale?.price ?? null, competitorsPrice);

  return {
    ...rowData,
    offerDetails,
    expirationDate: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    revenueForecastSevenDays: 13000,
    novaMargem,
    novaCompetitividade,
    retailCpi,
    // Enable Calculate button by default for all rows, regardless of existing demand forecast data
    // This allows users to recalculate or calculate for the first time without needing to edit prices first
    novoCalculo: true,
    ...(wholesale && {
      wholesale: {
        ...wholesale,
        newCompetitivenessPrice: wholesaleCpi,
        discountPercentage: wholesale.discountPercentage,
      },
    }),
  };
};

export const onSuccessGetDatapointsRevisao = (data: GetDatapointsRevisao.Response): GetDatapointsRevisao.Response => {
  return {
    ...data,
    content: data.content.map(onSuccessGetDatapointsRevisaoItem),
  };
};

export const getDatapointsRevisao = async (params: GetDatapointsRevisao.Params): Promise<GetDatapointsRevisao.Response> => {
  try {
    const { url, config } = GET_ALL_PRODUCTS(params);

    const { data } = await trackPromise(
      api.post<GetDatapointsRevisao.Response>(url, config.data, {
        expectToken: true,
        params: {
          sort: (config.params as { sort?: unknown; size?: unknown; page?: unknown }).sort,
          size: (config.params as { sort?: unknown; size?: unknown; page?: unknown }).size,
          page: (config.params as { sort?: unknown; size?: unknown; page?: unknown }).page,
        },
      }),
      'get-datapoints-revisao',
    );

    return onSuccessGetDatapointsRevisao(data);
  } catch (error) {
    return onSuccessGetDatapointsRevisao({
      content: [],
      number: 0,
      size: 25,
      totalElements: 0,
      hasContent: false,
      first: true,
      last: true,
      numberOfElements: 0,
      totalPages: 0,
      pageable: {
        sort: [],
        offset: 0,
        pageSize: 25,
        pageNumber: 0,
        paged: true,
        unpaged: false,
      },
      empty: true,
    });
  }
};

export type SaveEditedDatapoint = {
  data: Pick<RowData, 'productId' | 'storeId' | 'price' | 'productsToBePricedId'> & {
    datapointExhibitionType: string;
    clusterId: string | null;
  };
  response: AxiosResponse<RowData[] & { datapointExhibitionType: string }>;
};

export function saveEditedDatapoint({
  price,
  datapointExhibitionType,
  clusterId,
  ...data
}: SaveEditedDatapoint['data']): Promise<SaveEditedDatapoint['response']> {
  return withPendingEditedPrice(() =>
    trackPromise(
      // O tipo da chamada no axios 0.30 é AxiosPromise<R> com R = AxiosResponse<T>: sem informar R, a resposta vem embrulhada duas vezes.
      api<SaveEditedDatapoint['response']['data'], SaveEditedDatapoint['response']['data']>({
        url: `${process.env.REACT_APP_IPA_URL}/all-products/update/price`,
        method: 'POST',
        expectToken: true,
        params: getValidData({
          editedPrice: price,
          datapointExhibitionType,
          ...data,
          ...(datapointExhibitionType === 'PRODUCT_CLUSTER' || datapointExhibitionType === 'PRODUCT_CLUSTER_FAMILY' ? { clusterId } : {}),
        }),
        paramsSerializer(params) {
          return qs.stringify(params, { arrayFormat: 'repeat' });
        },
      })
        .then(resp => {
          return resp;
        })
        .catch(error => {
          throw error;
        }),
      `edit-datapoint-${data.productId}-${data.storeId}`,
    ),
  );
}

export namespace SaveEditedWholesaleDatapoint {
  export type Data = {
    productId: RowData['productId'];
    storeId: RowData['storeId'];
    price: RowData['price'];
    triggerCount: number | null;
  };
  export type Result = AxiosResponse<unknown>;
}

export const saveEditedWholesaleDatapoint = async (data: SaveEditedWholesaleDatapoint.Data): Promise<SaveEditedWholesaleDatapoint.Result> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { price, ...rest } = data;
  const updatedData = { ...rest, editedPrice: data.price };
  return withPendingEditedPrice(() =>
    api.post('/all-products/update/price/wholesale', updatedData, {
      expectToken: true,
    }),
  );
};

export namespace SaveEditedOfferDatapoint {
  export type Data = {
    productId: RowData['productId'];
    storeId: RowData['storeId'];
    price: RowData['price'];
    productFamilyId?: RowData['productFamilyId'];
    clusterId?: RowData['clusterId'];
    datapointExhibitionType: SaveEditedDatapoint['data']['datapointExhibitionType'];
  };
  export type Result = AxiosResponse<unknown>;
}

export const saveEditedOfferDatapoint = async (data: SaveEditedOfferDatapoint.Data): Promise<SaveEditedOfferDatapoint.Result> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { price, clusterId, datapointExhibitionType, productFamilyId, ...rest } = data;
  const updatedData = getValidData({
    ...rest,
    editedPrice: data.price,
    datapointExhibitionType,
    productFamilyId: productFamilyId ?? null,
    clusterId: clusterId ?? null,
  });
  return withPendingEditedPrice(() =>
    api.post('/all-products/update/price/offer', updatedData, {
      expectToken: true,
    }),
  );
};

export namespace UndoEditedDatapoint {
  export type Params = {
    productId: RowData['productId'];
    storeId: RowData['storeId'];
    productsToBePricedId: RowData['productsToBePricedId'];
  };
  export type Response = AxiosResponse<RowData>;
}

export function undoEditedDatapoint(params: UndoEditedDatapoint.Params) {
  return trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/all-products/undo-price-change`,
      method: 'DELETE',
      expectToken: true,
      params,
      paramsSerializer(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    }).then(resp => resp),
    `edit-datapoint-${params.productId}-${params.storeId}`,
  );
}

type OfferDetails = NonNullable<ExpandedRowPrice>;

export namespace UndoEditedOfferDatapoint {
  export type Params = {
    productId: RowData['productId'];
    storeId: RowData['storeId'];
    productFamilyId?: OfferDetails['productFamilyId'];
    clusterId?: OfferDetails['clusterId'];
    datapointExhibitionType: string;
  };
  /** Estado original da oferta devolvido pelo ipa-app (PriceOfferDetails). */
  export type PriceOfferDetails = Partial<
    Pick<
      OfferDetails,
      | 'storeId'
      | 'productFamilyId'
      | 'clusterId'
      | 'discountPercentage'
      | 'price'
      | 'retailPrice'
      | 'priceStatus'
      | 'newMargin'
      | 'originalMargin'
      | 'competitorsPrice'
      | 'competitivenessIndicator'
      | 'competitorDays'
      | 'productFamily'
      | 'clusterRule'
      | 'maxCpi'
      | 'minCpi'
      | 'maxMargin'
      | 'minMargin'
    >
  > & {
    id?: string;
    brokenLimit?: boolean | null;
  };
  export type Response = AxiosResponse<PriceOfferDetails>;
}

export function undoEditedOfferDatapoint({
  productFamilyId,
  clusterId,
  ...params
}: UndoEditedOfferDatapoint.Params): Promise<UndoEditedOfferDatapoint.Response> {
  return trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/all-products/undo-price-change/offer`,
      method: 'DELETE',
      expectToken: true,
      params: {
        ...params,
        ...(productFamilyId && { productFamilyId }),
        ...(clusterId && { clusterId }),
      },
      paramsSerializer(queryParams) {
        return qs.stringify(queryParams, { arrayFormat: 'repeat', skipNulls: true });
      },
    }),
    `edit-offer-datapoint-${params.productId}-${params.storeId}`,
  );
}

// Limites que o desfazer não calcula: a resposta traz null e não pode apagar o que a linha já tem.
const OFFER_LIMIT_KEYS = ['maxCpi', 'minCpi', 'maxMargin', 'minMargin'] as const;
// Identificadores que o desfazer pode devolver vazios (inclusive a string "null" de um UUID nulo).
const OFFER_ID_KEYS = ['clusterId', 'productFamilyId'] as const;

const isMissingId = (value: unknown) => value === null || value === undefined || value === 'null';

// Aplica a oferta devolvida pelo desfazer sobre a atual (o que não veio permanece) e recalcula o CPI
// como a grade faz ao carregar a linha.
export const mergeUndoneOfferDetails = (
  currentOfferDetails: RowData['offerDetails'],
  { brokenLimit, ...undoneOffer }: UndoEditedOfferDatapoint.PriceOfferDetails,
): RowData['offerDetails'] => {
  const offerDetails = { ...currentOfferDetails, ...undoneOffer } as OfferDetails;

  OFFER_LIMIT_KEYS.forEach(key => {
    if (undoneOffer[key] == null) offerDetails[key] = currentOfferDetails?.[key] ?? null;
  });
  OFFER_ID_KEYS.forEach(key => {
    if (isMissingId(undoneOffer[key])) offerDetails[key] = currentOfferDetails?.[key] ?? null;
  });

  return {
    ...offerDetails,
    newCompetitivenessPrice: IPAMaths.novaCompetitividade(offerDetails.price ?? null, offerDetails.competitorsPrice ?? null),
  };
};

export async function getTotalPrices(data?: PriceManagerDatapointsModel) {
  const response = await api.post<TotalPriceDataType>('/all-products/total-prices', data, {
    expectToken: true,
    paramsSerializer(params) {
      return qs.stringify(params, { arrayFormat: 'comma' });
    },
  });
  return response.data;
}

export async function getVariationPrices(data?: PriceManagerDatapointsModel) {
  const response = await trackPromise(
    api.post<VariationPriceDataType>('/all-products/variation-price', data, {
      expectToken: true,
      paramsSerializer(params) {
        return qs.stringify(params, { arrayFormat: 'comma' });
      },
    }),
    'variation-price',
  );
  return response.data;
}
